/**
 * Fix T / T1 — the ONE place that builds `TournamentStatusInput`. Before this,
 * `DetailsPanel/model.ts` and `stage/TournamentIdle/model.ts` each assembled
 * the input object by hand, off slightly different reads of the core
 * (`DetailsPanel`'s own `joined` local vs `TournamentIdle`'s
 * `join.code === 2 || arena.isJoined`) — exactly the kind of drift ONE status
 * MODEL (`tournamentStatusFrom`) cannot protect against on its own, because
 * the two renderers never agreed on the INPUT either. Both now call this.
 *
 * Pure, like `tournamentStatusFrom` itself: no hooks, no `Date.now()`.
 * `nowMs`/`myGameLive`/`opponentName` are the caller's own signals (the board
 * card has no live game of mine, by construction — G-x3) and `raw` is the
 * REST details, nullable for the very same reason.
 *
 * Typed on a `Pick`, not the full `LiveGameState`: the body only ever reads
 * `.tournament` / `.arena`, and a narrower parameter lets each caller keep
 * its OWN tight `useShallow` selection (`{tournament, arena}` object
 * references, which do not change per chess move) rather than subscribing to
 * the whole store just to satisfy this function's signature. Any real
 * `LiveGameState` still satisfies this type structurally.
 */
import type {LiveGameState} from '@/Store/liveGameStore';

import type {TournamentRawDetails} from '../detailsShape';
import type {ArenaStatusInput, TournamentStatusInput} from './tournamentStatus';

export type StatusInputCore = Pick<LiveGameState, 'tournament' | 'arena'>;

export type StatusInputExtras = {
  /** null on the board card, which has no query (G-x3) */
  raw: TournamentRawDetails | null;
  nowMs: number;
  /** a live board of MINE is on the stage */
  myGameLive: boolean;
  /** my opponent's display name when myGameLive, for the detail line */
  opponentName: string | null;
};

export function statusInputFrom(
  s: StatusInputCore,
  extras: StatusInputExtras,
): TournamentStatusInput {
  const family = s.tournament.mode === 'arena' ? 'arena' : 'swiss';
  const status = s.arena.status;
  const standings = s.arena.standings;
  const arena: ArenaStatusInput | null =
    family === 'arena'
      ? {
          state: status?.state || standings?.state || null,
          myState: status?.myState ?? null,
          paused: !!status?.paused || status?.myState === 'paused',
          endsAtMs: status?.endsAtMs || standings?.endsAtMs || 0,
          rank: status?.rank ?? 0,
          points: status?.points ?? 0,
          poolSize: status?.poolSize ?? 0,
        }
      : null;

  return {
    nowMs: extras.nowMs,
    raw: extras.raw,
    // Fix T2: `arena.isJoined` is a non-nullable boolean (`state.ts`), so a
    // `??` chain never reaches the REST fallback — `||` is what the spec's
    // intent ("fall back to whatever proves membership") actually needs.
    joined:
      s.tournament.joinedLocal ?? (s.arena.isJoined || !!extras.raw?.is_joined),
    withdrawn: s.tournament.withdraw.status === 'ok',
    ended: s.tournament.ended,
    roundState: s.tournament.roundState,
    statusMessage: s.tournament.statusMessage,
    currentRound: s.tournament.currentRound,
    pairingGenerated: s.tournament.pairingGenerated,
    ongoingGames: s.tournament.ongoingGames,
    roundStartAt: s.tournament.roundStartAt,
    nextRound: s.tournament.nextRound,
    bye: s.tournament.bye,
    myGameLive: extras.myGameLive,
    opponentName: extras.opponentName,
    family,
    arena,
  };
}

export default statusInputFrom;
